// What the assistant remembers between conversations: summaries it wrote when
// a spoken conversation ended, and notes you asked it to keep. Each can be
// forgotten, and a note can be added by hand.
import { useEffect, useState, type FormEvent } from 'react'
import { X } from 'lucide-react'
import { api } from '@/runtime'
import type { MemoryItem } from '@/db/types'
import { dateOf, fmtDateShort } from '@/lib/time'
import { Button, cx } from './ui'
import { Phrases } from './Phrases'

export const MemoryList = () => {
  const [items, setItems] = useState<MemoryItem[] | null>(null)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let stale = false
    api
      .voiceMemory()
      .then((r) => {
        if (!stale) setItems(r.items)
      })
      .catch((e: Error) => {
        if (!stale) setError(e.message)
      })
    return () => {
      stale = true
    }
  }, [])
  const add = async (e: FormEvent) => {
    e.preventDefault()
    const t = note.trim()
    if (!t) return
    try {
      const r = await api.voiceRemember(t)
      setItems(r.items)
      setNote('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save')
    }
  }
  const forget = async (at: string) => {
    try {
      const r = await api.voiceForget(at)
      setItems(r.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not forget')
    }
  }
  return (
    <div className="flex flex-col gap-3" data-testid="memory-list">
      <p className={cx('text-faint', 'text-xs')}>
        Summaries of spoken conversations and notes you asked it to keep. Every conversation, spoken or typed,
        starts with these.
      </p>
      {error && <p className="text-sm text-amber-300/90">{error}</p>}
      {items && items.length === 0 && <p className={cx('text-faint', 'text-sm')}>Nothing yet.</p>}
      {items && items.length > 0 && (
        <ul className="flex flex-col divide-y divide-line rounded-[var(--radius-card-inner)] border border-line overflow-hidden">
          {items
            .slice()
            .reverse()
            .map((it) => (
              <li key={it.at} className={cx('flex items-start gap-3 px-3', 'py-2')} data-testid="memory-item">
                <span className="min-w-0 flex-1">
                  <span className={cx('block', 'text-sm')}>{it.text}</span>
                  {/* `at` is a full timestamp: its local day is what fmtDateShort reads */}
                  <Phrases
                    className="text-faint text-xs"
                    parts={[
                      fmtDateShort(dateOf(new Date(it.at))),
                      it.kind === 'note' ? 'A note' : 'A conversation',
                    ]}
                  />
                </span>
                <button
                  type="button"
                  onClick={() => void forget(it.at)}
                  aria-label="Forget this"
                  className="shrink-0 text-faint hover:text-fg p-1"
                >
                  <X size={14} />
                </button>
              </li>
            ))}
        </ul>
      )}
      <form onSubmit={(e) => void add(e)} className="flex gap-2">
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Something to remember…"
          className="flex-1 min-w-0"
          data-testid="memory-note"
        />
        <Button type="submit" disabled={!note.trim()}>
          Keep
        </Button>
      </form>
    </div>
  )
}
